import { Injectable } from '@angular/core';
import { HttpClient, HttpBackend } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap, timeout } from 'rxjs/operators';
import { getCountryCallingCode, CountryCode } from 'libphonenumber-js';
import { environment } from '../../../environments/environment';

export interface ShippingCountry {
  code: string;
  name: string;
}

export interface PostalLookupResult {
  valid: boolean;
  city?: string;
  state?: string;
}

import validator from 'validator';

@Injectable({
  providedIn: 'root',
})
export class ShippingConfigService {
  private countries$?: Observable<ShippingCountry[]>;
  private cachedCountries: ShippingCountry[] = [];
  private zippoSupportedCountries$?: Observable<Set<string>>;
  private readonly zippoHttp: HttpClient;

  /**
   * Returns international telephone dial code for a given country ISO or name dynamically using libphonenumber-js
   */
  getDialCode(countryCodeOrName: string): string {
    const iso = (this.resolveCountryCode(countryCodeOrName) || countryCodeOrName || '').toUpperCase();
    try {
      if (iso && iso.length === 2) {
        const code = getCountryCallingCode(iso as CountryCode);
        return code ? `+${code}` : '+';
      }
    } catch (_) {
      // Unrecognized code fallback
    }
    return '+';
  }

  constructor(
    private readonly http: HttpClient,
    handler: HttpBackend
  ) {
    // Dedicated client using HttpBackend to bypass auth/caching interceptors for 3rd party APIs
    this.zippoHttp = new HttpClient(handler);
    // Pre-fetch Zippopotam supported country directory in background
    this.getZippoSupportedCountries().subscribe({
      error: () => {},
    });
  }

  /**
   * Fetches country list from backend GET /api/shipping/countries and caches in memory.
   * If backend is not yet available, falls back to browser standard Intl country list.
   */
  getCountries(): Observable<ShippingCountry[]> {
    if (!this.countries$) {
      const url = `${environment.apiUrl}/shipping/countries`;
      this.countries$ = this.http.get<any>(url).pipe(
        map((res: any) => {
          const rawList = Array.isArray(res) ? res : res?.data || res?.countries || [];
          const normalized: ShippingCountry[] = rawList
            .map((item: any) => ({
              code: String(item.code || item.country_code || item.countryCode || '').trim().toUpperCase(),
              name: String(item.name || item.country || item.countryName || '').trim(),
            }))
            .filter((item: ShippingCountry) => item.code.length > 0 && item.name.length > 0);

          if (normalized.length > 0) {
            this.cachedCountries = normalized;
            return normalized;
          }
          const intlList = this.getIntlCountries();
          this.cachedCountries = intlList;
          return intlList;
        }),
        catchError(() => {
          const intlList = this.getIntlCountries();
          this.cachedCountries = intlList;
          return of(intlList);
        }),
        shareReplay(1)
      );
    }
    return this.countries$;
  }

  /**
   * Resolves a country name or code to a 2-letter uppercase ISO code
   */
  resolveCountryCode(country: string): string {
    const clean = (country || '').trim();

    if (!clean) {
      return '';
    }

    if (clean.length === 2) {
      return clean.toUpperCase();
    }

    if (this.cachedCountries.length > 0) {
      const match = this.cachedCountries.find(
        (c) =>
          c.name.toLowerCase() === clean.toLowerCase() ||
          c.code.toUpperCase() === clean.toUpperCase()
      );
      if (match) {
        return match.code.toUpperCase();
      }
    }

    try {
      if (typeof Intl !== 'undefined' && (Intl as any).DisplayNames) {
        const displayNames = new Intl.DisplayNames(['en'], { type: 'region' });
        for (let i = 65; i <= 90; i++) {
          for (let j = 65; j <= 90; j++) {
            const code = String.fromCharCode(i, j);
            try {
              const name = displayNames.of(code);
              if (name && name.toLowerCase() === clean.toLowerCase()) {
                return code;
              }
            } catch (_) {}
          }
        }
      }
    } catch (_) {}

    return '';
  }

  /**
   * Fetches the set of country ISO codes supported by Zippopotam from their official directory.
   */
  getZippoSupportedCountries(): Observable<Set<string>> {
    if (!this.zippoSupportedCountries$) {
      this.zippoSupportedCountries$ = this.zippoHttp
        .get('https://api.zippopotam.us/', { responseType: 'text' })
        .pipe(
          timeout(4000),
          map((html: string) => {
            const set = new Set<string>();
            const regex = /<tr>\s*<td>[^<]+<\/td>\s*<td>([A-Z]{2})<\/td>/gi;
            let match: RegExpExecArray | null;
            while ((match = regex.exec(html)) !== null) {
              if (match[1]) {
                set.add(match[1].toUpperCase());
              }
            }
            return set;
          }),
          catchError(() => {
            return of(new Set<string>());
          }),
          shareReplay(1)
        );
    }
    return this.zippoSupportedCountries$;
  }

  /**
   * Validates postal code for a country.
   * 1. Checks strict country-specific postal format regex (e.g. 4 digits for TN).
   * 2. If country is in Zippopotam directory, validates against Zippopotam database.
   * 3. Returns false on invalid format or non-existent postal code.
   */
  validatePostalCode(countryCode: string, postalCode: string): Observable<boolean> {
    const isoCode = this.resolveCountryCode(countryCode);
    const cleanPostal = postalCode?.trim();

    if (!cleanPostal) {
      return of(false);
    }

    if (!isoCode || isoCode.length !== 2) {
      return of(false);
    }

    // Step 1: Dynamic postal format validation using validator package
    const isSupportedLocale = (validator.isPostalCodeLocales as readonly string[]).includes(isoCode);
    const isValidFormat = isSupportedLocale
      ? validator.isPostalCode(cleanPostal, isoCode as validator.PostalCodeLocale)
      : validator.isPostalCode(cleanPostal, 'any');

    if (!isValidFormat) {
      return of(false);
    }

    return this.lookupPostalCode(countryCode, postalCode).pipe(map((res) => res.valid));
  }

  /**
   * Validates postal code and extracts verified City & State/Province for auto-filling
   */
  lookupPostalCode(countryCode: string, postalCode: string): Observable<PostalLookupResult> {
    const isoCode = this.resolveCountryCode(countryCode);
    const cleanPostal = postalCode?.trim();

    if (!cleanPostal || !isoCode || isoCode.length !== 2) {
      return of({ valid: false });
    }

    // Step 1: Dynamic postal format validation using validator package
    const isSupportedLocale = (validator.isPostalCodeLocales as readonly string[]).includes(isoCode);
    const isValidFormat = isSupportedLocale
      ? validator.isPostalCode(cleanPostal, isoCode as validator.PostalCodeLocale)
      : validator.isPostalCode(cleanPostal, 'any');

    if (!isValidFormat) {
      return of({ valid: false });
    }

    // Step 2: Zippopotam verification & place extraction
    return this.getZippoSupportedCountries().pipe(
      switchMap((supportedSet) => {
        // If country not covered by Zippopotam (e.g. TN, EG, SA), format check passed
        if (supportedSet.size > 0 && !supportedSet.has(isoCode)) {
          return of({ valid: true });
        }

        const url = `https://api.zippopotam.us/${encodeURIComponent(isoCode)}/${encodeURIComponent(cleanPostal)}`;

        return this.zippoHttp.get<any>(url).pipe(
          timeout(5000),
          map((data: any) => {
            const place = Array.isArray(data?.places) && data.places.length > 0 ? data.places[0] : null;
            let city = place?.['place name'] || undefined;
            // Clean up postal district suffix if present, e.g. "Paris 01 Louvre" -> "Paris"
            if (city && city.includes('Paris') && /Paris\s+\d+/i.test(city)) {
              city = 'Paris';
            }
            const state = place?.['state'] || undefined;
            return {
              valid: true,
              city,
              state,
            };
          }),
          catchError((error: any) => {
            if (error.status === 404) {
              if (supportedSet.size > 0 && supportedSet.has(isoCode)) {
                return of({ valid: false });
              }
              return of({ valid: true });
            }
            return of({ valid: true });
          })
        );
      }),
      catchError(() => of({ valid: true }))
    );
  }

  /**
   * Dynamically generates country list using browser's Intl API without any hardcoded country literals
   */
  private getIntlCountries(): ShippingCountry[] {
    const list: ShippingCountry[] = [];
    try {
      if (typeof Intl !== 'undefined' && (Intl as any).DisplayNames) {
        const displayNames = new Intl.DisplayNames(['en'], { type: 'region' });
        for (let i = 65; i <= 90; i++) {
          for (let j = 65; j <= 90; j++) {
            const code = String.fromCharCode(i, j);
            try {
              const name = displayNames.of(code);
              if (name && name !== code && !/^\d+$/.test(name)) {
                list.push({ code, name });
              }
            } catch (_) {}
          }
        }
      }
    } catch (_) {}

    return list.sort((a, b) => a.name.localeCompare(b.name));
  }
}
